import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  accountingApi,
  formatInr,
  moneyNum,
  type AccountingVoucher,
} from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

export default function AccountingVoucherDetailPage() {
  const { voucherId } = useParams();
  const { hospitalId, canPost } = useAccountingHospitalContext();
  const qc = useQueryClient();

  const { data: voucher, isLoading } = useQuery({
    queryKey: ["accounting-voucher", hospitalId, voucherId],
    queryFn: () => accountingApi.getVoucher(voucherId!, hospitalId),
    enabled: !!hospitalId && !!voucherId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["accounting-voucher", hospitalId, voucherId] });
    qc.invalidateQueries({ queryKey: ["accounting-vouchers", hospitalId] });
    qc.invalidateQueries({ queryKey: ["accounting-dashboard", hospitalId] });
    qc.invalidateQueries({ queryKey: ["accounting-tb", hospitalId] });
  };

  const postMut = useMutation({
    mutationFn: () => accountingApi.postVoucher(voucherId!, hospitalId),
    onSuccess: () => {
      toast.success("Voucher posted");
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Post failed"),
  });

  const reverseMut = useMutation({
    mutationFn: () =>
      accountingApi.reverseVoucher(voucherId!, hospitalId, "Manual reversal"),
    onSuccess: () => {
      toast.success("Voucher reversed");
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Reverse failed"),
  });

  const cancelMut = useMutation({
    mutationFn: () => accountingApi.cancelVoucher(voucherId!, hospitalId),
    onSuccess: () => {
      toast.success("Draft cancelled");
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Cancel failed"),
  });

  if (isLoading || !voucher) {
    return <p className="text-sm text-slate-500">Loading voucher…</p>;
  }

  const v = voucher as AccountingVoucher;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-1">
            <Link to="..">← Vouchers</Link>
          </Button>
          <h2 className="text-lg font-medium">{v.voucherNumber}</h2>
          <p className="text-sm text-slate-500">
            {String(v.voucherDate).slice(0, 10)} · {v.voucherType} ·{" "}
            <Badge className="align-middle">{v.status}</Badge>
          </p>
          {v.narration ? (
            <p className="mt-1 text-sm text-slate-700">{v.narration}</p>
          ) : null}
        </div>
        {canPost ? (
          <div className="flex flex-wrap gap-2">
            {v.status === "DRAFT" ? (
              <>
                <Button
                  size="sm"
                  disabled={postMut.isPending}
                  onClick={() => postMut.mutate()}
                >
                  Post
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={cancelMut.isPending}
                  onClick={() => cancelMut.mutate()}
                >
                  Cancel draft
                </Button>
              </>
            ) : null}
            {v.status === "POSTED" ? (
              <Button
                size="sm"
                variant="destructive"
                disabled={reverseMut.isPending}
                onClick={() => {
                  if (confirm("Reverse this posted voucher?")) reverseMut.mutate();
                }}
              >
                Reverse
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Account</TableHead>
              <TableHead className="text-right">Debit</TableHead>
              <TableHead className="text-right">Credit</TableHead>
              <TableHead>Narration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(v.lines || []).map((line) => (
              <TableRow key={line.id}>
                <TableCell>{line.lineNo}</TableCell>
                <TableCell>
                  {line.ledgerAccount
                    ? `${line.ledgerAccount.code} — ${line.ledgerAccount.name}`
                    : line.ledgerAccountId}
                </TableCell>
                <TableCell className="text-right">
                  {moneyNum(line.debit) ? formatInr(moneyNum(line.debit)) : ""}
                </TableCell>
                <TableCell className="text-right">
                  {moneyNum(line.credit) ? formatInr(moneyNum(line.credit)) : ""}
                </TableCell>
                <TableCell>{line.narration || ""}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2} className="font-medium">
                Total
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatInr(moneyNum(v.totalDebit))}
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatInr(moneyNum(v.totalCredit))}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
